"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useDisclosure } from "@heroui/react";

import { useReservationForm } from "./hooks/useReservationForm";
import { ReservationStep2 } from "./ReservationStep2";
import { RESERVATION_UI } from "./reservationFormUi";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { TripTypeSelector } from "./reservation-form/TripTypeSelector";
import { TripDetailsSection } from "./reservation-form/TripDetailsSection";
import { PassengersSection } from "./reservation-form/PassengersSection";
import { SubmitBar } from "./reservation-form/SubmitBar";

type TripType = "one-way" | "round-trip" | "multi-trip" | "disposition";

type ReservationFormProps = {
  onStepChange?: (step: 1 | 2) => void;
  /** Langue du formulaire — transmise à l'API pour l'email client (fr défaut). */
  locale?: Locale;
};

export const ReservationForm = ({
  onStepChange,
  locale = "fr",
}: ReservationFormProps) => {
  const t = tr(RESERVATION_UI, locale).step1;
  const router = useRouter();
  const [step, setStep] = useState(1);
  const formRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [customerInfo, setCustomerInfo] = useState<{
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reserveActionError, setReserveActionError] = useState<string | null>(
    null,
  );
  const [estimateActionError, setEstimateActionError] = useState<string | null>(
    null,
  );

  const {
    formData,
    setFormData,
    departureSuggestions,
    arrivalSuggestions,
    stopSuggestions,
    showDepartureSuggestions,
    showArrivalSuggestions,
    showStopSuggestions,
    setShowDepartureSuggestions,
    setShowArrivalSuggestions,
    setShowStopSuggestions,
    isLoadingDeparture,
    isLoadingArrival,
    isLoadingStop,
    handleAddressInput,
    selectSuggestion,
    filterByPOIType,
    calculatePrice,
    isCalculatingPrice,
    estimatedPrice,
    priceError,
    addStop,
    removeStop,
    updateStop,
    updateStopWaitingTime,
    submitReservation,
    isSubmitting,
    submitError,
    selectedVehicle,
  } = useReservationForm(false, locale);

  const [tripType, setTripType] = useState<TripType>("one-way");
  const [customizeReturn, setCustomizeReturn] = useState(false);
  // Garde anti-double-soumission pour le bouton « Réserver » (handleSubmit est
  // asynchrone : sans ce verrou, un double-clic rapide déclenche 2 calculs +
  // 2 transitions). Le ref bloque la ré-entrée AVANT le prochain rendu.
  const [isReserving, setIsReserving] = useState(false);
  const isReservingRef = useRef(false);
  const [showAdvancedPassengerOptions, setShowAdvancedPassengerOptions] =
    useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    departure?: string;
    arrival?: string;
    date?: string;
    time?: string;
    returnDate?: string;
    returnTime?: string;
  }>({});

  // Source de vérité unique : on met à jour tripType ET formData dans le même
  // gestionnaire de clic (plus d'effets miroir qui re-seedaient les champs de
  // retour à chaque rendu et pouvaient écraser une saisie utilisateur).
  const handleTripTypeChange = (next: TripType) => {
    setTripType(next);
    setFormData((prev) => ({
      ...prev,
      tripType: next,
      // Pré-remplissage des champs de retour UNIQUEMENT au passage en aller-
      // retour, et seulement si l'utilisateur ne les a pas déjà renseignés.
      ...(next === "round-trip"
        ? {
            returnDate: prev.returnDate || prev.date,
            returnTime: prev.returnTime || prev.time,
            returnArrival: prev.returnArrival || prev.departure,
          }
        : {}),
    }));
  };

  const validateForm = () => {
    const errors: typeof validationErrors = {};

    if (!formData.departure) {
      errors.departure = t.v.departure;
    }
    if (!formData.date) {
      errors.date = t.v.date;
    }
    if (!formData.time) {
      errors.time = t.v.time;
    }
    if (tripType !== "disposition" && !formData.arrival) {
      errors.arrival = t.v.arrival;
    }
    if (tripType === "round-trip") {
      if (!formData.returnDate) {
        errors.returnDate = t.v.returnDate;
      }
      if (!formData.returnTime) {
        errors.returnTime = t.v.returnTime;
      }
    }

    setValidationErrors(errors);
    return errors;
  };

  const buildStep1ErrorSummary = (errors: typeof validationErrors): string => {
    const messages = Object.values(errors).filter(
      (message): message is string => Boolean(message),
    );
    return messages.join(" ");
  };

  const handleEstimatePrice = async () => {
    setEstimateActionError(null);
    setReserveActionError(null);
    const result = await calculatePrice();
    if (result.amount === null) {
      setEstimateActionError(result.error || t.estimateError);
    }
  };

  const handleSubmit = async () => {
    // Verrou de ré-entrée : ignore tout clic tant qu'une soumission est en cours.
    if (isReservingRef.current) return;
    isReservingRef.current = true;
    setIsReserving(true);

    setError(null);
    setReserveActionError(null);

    try {
      const validationResult = validateForm();
      if (Object.keys(validationResult).length > 0) {
        setReserveActionError(buildStep1ErrorSummary(validationResult));
        return;
      }

      // Si on est en mode multi-trajets et qu'il n'y a pas d'étapes, on propose de passer en aller simple
      if (tripType === "multi-trip" && formData.stops.length === 0) {
        onOpen();
        return;
      }

      // Recalcul déterministe pour éviter les états asynchrones obsolètes
      const calculatedPrice = await calculatePrice();
      if (calculatedPrice.amount === null) {
        setError(calculatedPrice.error || priceError || t.priceCalcError);
        return;
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
      setStep(2);
    } finally {
      isReservingRef.current = false;
      setIsReserving(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setValidationErrors({});
    setReserveActionError(null);
    setEstimateActionError(null);
  };

  const handleSwitchToOneWay = () => {
    handleTripTypeChange("one-way");
    onClose();
    window.scrollTo({ top: 0, behavior: "smooth" });
    setStep(2);
  };

  const handleStep2Submit = async (data: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    countryCode: string;
    company?: string;
  }) => {
    setError(null);
    setReserveActionError(null);
    try {
      const result = await submitReservation(data);
      if (result.success) {
        const params = new URLSearchParams({
          type: formData.tripType,
          date: formData.date,
          time: formData.time,
          price: String(Math.round(estimatedPrice ?? 0)),
          // SLA dérivé UNIQUEMENT du caractère immédiat de l'heure choisie : une
          // réservation datée dans le futur ne doit jamais être taguée urgente.
          sla: String(formData.time).startsWith("now:") ? "au-plus-tot" : "48h",
        });
        router.push(`/reservation/succes?${params.toString()}`);
      } else {
        setError(result.error || t.reservationError);
      }
    } catch (error) {
      setError(t.unexpectedError);
    }
  };

  useEffect(() => {
    // Complètement désactiver le défilement automatique
    // Ne rien faire ici
  }, [step]);

  useEffect(() => {
    onStepChange?.(step as 1 | 2);
  }, [step, onStepChange]);

  if (step === 2) {
    return (
      <ReservationStep2
        key="step2"
        onSubmit={handleStep2Submit}
        onBack={handleBack}
        isSubmitting={isSubmitting}
        error={error || submitError}
        locale={locale}
      />
    );
  }

  return (
    <div ref={formRef}>
      <AnimatePresence mode="wait" onExitComplete={() => setIsAnimating(false)}>
        {step === 2 ? (
          <ReservationStep2
            key="step2"
            onSubmit={handleStep2Submit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            error={error || submitError}
          />
        ) : (
          <motion.div
            key="step1"
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            onAnimationStart={() => setIsAnimating(true)}
            className="mx-auto w-full max-w-4xl rounded-2xl border border-black/10 bg-white p-4 text-black shadow-[0_10px_32px_rgba(0,0,0,0.12)] sm:p-8"
          >
            <TripTypeSelector
              t={t}
              tripType={tripType}
              onTripTypeChange={handleTripTypeChange}
              formData={formData}
              setFormData={setFormData}
              customizeReturn={customizeReturn}
              setCustomizeReturn={setCustomizeReturn}
            />

            <TripDetailsSection
              t={t}
              locale={locale}
              tripType={tripType}
              formData={formData}
              setFormData={setFormData}
              departureSuggestions={departureSuggestions}
              arrivalSuggestions={arrivalSuggestions}
              stopSuggestions={stopSuggestions}
              showDepartureSuggestions={showDepartureSuggestions}
              showArrivalSuggestions={showArrivalSuggestions}
              showStopSuggestions={showStopSuggestions}
              setShowDepartureSuggestions={setShowDepartureSuggestions}
              setShowArrivalSuggestions={setShowArrivalSuggestions}
              setShowStopSuggestions={setShowStopSuggestions}
              isLoadingDeparture={isLoadingDeparture}
              isLoadingArrival={isLoadingArrival}
              isLoadingStop={isLoadingStop}
              handleAddressInput={handleAddressInput}
              selectSuggestion={selectSuggestion}
              filterByPOIType={filterByPOIType}
              addStop={addStop}
              removeStop={removeStop}
              updateStop={updateStop}
              updateStopWaitingTime={updateStopWaitingTime}
              customizeReturn={customizeReturn}
            />

            <PassengersSection
              t={t}
              locale={locale}
              formData={formData}
              setFormData={setFormData}
              selectedVehicle={selectedVehicle}
              showAdvancedPassengerOptions={showAdvancedPassengerOptions}
              setShowAdvancedPassengerOptions={setShowAdvancedPassengerOptions}
            />

            <SubmitBar
              t={t}
              onEstimatePrice={handleEstimatePrice}
              onSubmit={handleSubmit}
              isReserving={isReserving}
              isCalculatingPrice={isCalculatingPrice}
              estimatedPrice={estimatedPrice}
              priceError={priceError}
              estimateActionError={estimateActionError}
              reserveActionError={reserveActionError}
              isModalOpen={isOpen}
              onModalClose={onClose}
              onSwitchToOneWay={handleSwitchToOneWay}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
